import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Slider,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Backdrop,
  Menu,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Map as MapIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  GridOn as GridIcon,
  Layers as LayersIcon,
  Place as PlaceIcon,
  CheckCircle as AvailableIcon,
  Cancel as OccupiedIcon,
  Build as MaintenanceIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  AutoFixHigh as AutoGenerateIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ContentCopy as CloneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LocationOn as LocationIcon,
  FilterList as FilterIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as ExitFullscreenIcon,
  LockOpen as UnlockIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SiteMapCanvas from '../components/SiteMapCanvas';
import SiteMapToolbar from '../components/SiteMapToolbar';
import SiteDetailsDialog from '../components/SiteDetailsDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sitemap-tabpanel-${index}`}
      aria-labelledby={`sitemap-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SiteMapPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [siteMaps, setSiteMaps] = useState<any[]>([]);
  const [selectedMap, setSelectedMap] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Map state
  const [mapData, setMapData] = useState<any>(null);
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [hoveredSite, setHoveredSite] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<any>(null);
  
  // Editor state
  const [editMode, setEditMode] = useState<'view' | 'edit' | 'add'>('view');
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [showLayers, setShowLayers] = useState({
    sites: true,
    features: true,
    zones: true,
    labels: true
  });
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Filters
  const [checkInDate, setCheckInDate] = useState<any>(null);
  const [checkOutDate, setCheckOutDate] = useState<any>(null);
  const [siteTypeFilter, setSiteTypeFilter] = useState('all');
  const [amenityFilter, setAmenityFilter] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  
  // Dialogs
  const [siteDetailsDialog, setSiteDetailsDialog] = useState(false);
  const [selectedSiteDetails, setSelectedSiteDetails] = useState<any>(null);
  const [newMapDialog, setNewMapDialog] = useState(false);
  const [autoGenerateDialog, setAutoGenerateDialog] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [publishDialog, setPublishDialog] = useState(false);
  
  // Form state
  const [newMapForm, setNewMapForm] = useState({
    name: '',
    description: '',
    width: 1200,
    height: 800
  });
  
  const [autoGenerateForm, setAutoGenerateForm] = useState({
    mapWidth: 1200,
    mapHeight: 800,
    padding: 50,
    spacing: 20,
    groupByType: true
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchSiteMaps();
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedMap) {
      fetchMapData();
    }
  }, [selectedMap, checkInDate, checkOutDate]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      const locationData = response.data.locations || response.data || [];
      setLocations(Array.isArray(locationData) ? locationData : []);
      if (locationData.length > 0) {
        setSelectedLocation(locationData[0]._id);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchSiteMaps = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/site-maps/location/${selectedLocation}`);
      const mapsData = response.data || [];
      setSiteMaps(Array.isArray(mapsData) ? mapsData : []);
      
      // Select published map by default
      const publishedMap = mapsData.find((map: any) => map.metadata?.published);
      if (publishedMap) {
        setSelectedMap(publishedMap);
      } else if (mapsData.length > 0) {
        setSelectedMap(mapsData[0]);
      }
    } catch (error) {
      console.error('Error fetching site maps:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMapData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (checkInDate) params.append('checkIn', checkInDate.toISOString());
      if (checkOutDate) params.append('checkOut', checkOutDate.toISOString());
      
      const response = await api.get(`/site-maps/${selectedMap._id}?${params}`);
      setMapData(response.data);
      addToHistory(response.data);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMap = async () => {
    try {
      const response = await api.post('/site-maps', {
        ...newMapForm,
        location: selectedLocation,
        dimensions: {
          width: newMapForm.width,
          height: newMapForm.height,
          unit: 'pixels'
        }
      });
      
      setNewMapDialog(false);
      fetchSiteMaps();
      setSelectedMap(response.data);
    } catch (error) {
      console.error('Error creating map:', error);
    }
  };

  const handleAutoGenerate = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/site-maps/location/${selectedLocation}/auto-generate`,
        { options: autoGenerateForm }
      );
      
      setAutoGenerateDialog(false);
      fetchSiteMaps();
      setSelectedMap(response.data);
    } catch (error: any) {
      console.error('Error auto-generating map:', error);
      console.error('Error response:', error.response?.data);
      alert(`Error auto-generating map: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMap = async () => {
    try {
      setSaving(true);
      await api.put(`/site-maps/${selectedMap._id}`, mapData);
      
      alert('Map saved successfully!');
    } catch (error) {
      console.error('Error saving map:', error);
      alert('Error saving map');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishMap = async () => {
    try {
      await api.post(`/site-maps/${selectedMap._id}/publish`, {});
      
      setPublishDialog(false);
      fetchSiteMaps();
      alert('Map published successfully!');
    } catch (error) {
      console.error('Error publishing map:', error);
    }
  };

  const handleSiteClick = async (siteId: string) => {
    if (editMode === 'view') {
      try {
        const response = await api.get(
          `/site-maps/${selectedMap._id}/sites/${siteId}/details`
        );
        setSelectedSiteDetails(response.data);
        setSiteDetailsDialog(true);
      } catch (error) {
        console.error('Error fetching site details:', error);
      }
    } else if (editMode === 'edit') {
      if (selectedSites.has(siteId)) {
        selectedSites.delete(siteId);
      } else {
        selectedSites.add(siteId);
      }
      setSelectedSites(new Set(selectedSites));
    }
  };

  const handleSitePositionUpdate = (siteId: string, newPosition: any) => {
    if (editMode !== 'edit') return;
    
    const updatedMapData = { ...mapData };
    const siteIndex = updatedMapData.sites.findIndex((s: any) => s.site._id === siteId);
    if (siteIndex !== -1) {
      updatedMapData.sites[siteIndex].position = newPosition;
      setMapData(updatedMapData);
      addToHistory(updatedMapData);
    }
  };

  const addToHistory = (data: any) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(data)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setMapData(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setMapData(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(zoom * 1.2, 2));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom / 1.2, 0.5));
  };

  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const speedDialActions = [
    { icon: <SaveIcon />, name: 'Save', onClick: handleSaveMap },
    { icon: <PublishIcon />, name: 'Publish', onClick: () => setPublishDialog(true) },
    { icon: <ExportIcon />, name: 'Export', onClick: () => setExportDialog(true) },
    { icon: <AutoGenerateIcon />, name: 'Auto Generate', onClick: () => setAutoGenerateDialog(true) }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Interactive Site Map
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                label="Location"
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations && locations.map((location) => (
                  <MenuItem key={location._id} value={location._id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationIcon fontSize="small" />
                      <span>{location.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setNewMapDialog(true)}
            >
              New Map
            </Button>
          </Stack>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Visual site selection and management for your RV park
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          <Paper sx={{ p: 2, height: '80vh', position: 'relative' }}>
            <SiteMapToolbar
              editMode={editMode}
              setEditMode={setEditMode}
              showGrid={showGrid}
              setShowGrid={setShowGrid}
              snapToGrid={snapToGrid}
              setSnapToGrid={setSnapToGrid}
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onResetView={handleResetView}
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onSave={handleSaveMap}
              saving={saving}
            />
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : mapData ? (
              <SiteMapCanvas
                mapData={mapData}
                zoom={zoom}
                pan={pan}
                setPan={setPan}
                showGrid={showGrid}
                snapToGrid={snapToGrid}
                showLayers={showLayers}
                selectedSites={selectedSites}
                hoveredSite={hoveredSite}
                setHoveredSite={setHoveredSite}
                onSiteClick={handleSiteClick}
                onSitePositionUpdate={handleSitePositionUpdate}
                editMode={editMode}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="h6" color="text.secondary">
                  No map selected
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} lg={3}>
          <Paper>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="Maps" icon={<MapIcon />} iconPosition="start" />
              <Tab label="Filters" icon={<FilterIcon />} iconPosition="start" />
              <Tab label="Layers" icon={<LayersIcon />} iconPosition="start" />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
              {/* Maps List */}
              <List>
                {siteMaps && siteMaps.map((map) => (
                  <ListItem
                    key={map._id}
                    button
                    selected={selectedMap?._id === map._id}
                    onClick={() => setSelectedMap(map)}
                  >
                    <ListItemIcon>
                      <MapIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={map.name}
                      secondary={
                        <Stack direction="row" spacing={1}>
                          {map.metadata?.published && (
                            <Chip label="Published" size="small" color="success" />
                          )}
                          <Typography variant="caption">
                            {map.sites?.length || 0} sites
                          </Typography>
                        </Stack>
                      }
                    />
                    {map.metadata?.published && (
                      <ListItemSecondaryAction>
                        <VisibilityIcon color="success" />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
              
              {siteMaps.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No maps created yet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AutoGenerateIcon />}
                    onClick={() => setAutoGenerateDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Auto Generate Map
                  </Button>
                </Box>
              )}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              {/* Filters */}
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Availability Dates
                  </Typography>
                  <Stack spacing={2}>
                    <DatePicker
                      label="Check In"
                      value={checkInDate}
                      onChange={setCheckInDate}
                      sx={{ width: '100%' }}
                    />
                    <DatePicker
                      label="Check Out"
                      value={checkOutDate}
                      onChange={setCheckOutDate}
                      minDate={checkInDate}
                      sx={{ width: '100%' }}
                    />
                  </Stack>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Site Type
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={siteTypeFilter}
                      onChange={(e) => setSiteTypeFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="RV Site">RV Site</MenuItem>
                      <MenuItem value="Tent Site">Tent Site</MenuItem>
                      <MenuItem value="Cabin">Cabin</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Range
                  </Typography>
                  <Slider
                    value={priceRange}
                    onChange={(e, v) => setPriceRange(v as [number, number])}
                    valueLabelDisplay="auto"
                    min={0}
                    max={500}
                    marks={[
                      { value: 0, label: '$0' },
                      { value: 250, label: '$250' },
                      { value: 500, label: '$500' }
                    ]}
                  />
                </Box>
                
                <Button variant="outlined" fullWidth>
                  Apply Filters
                </Button>
              </Stack>
            </TabPanel>
            
            <TabPanel value={tabValue} index={2}>
              {/* Layers */}
              <List>
                <ListItem>
                  <ListItemIcon>
                    <PlaceIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sites" />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={showLayers.sites}
                      onChange={(e) => setShowLayers({ ...showLayers, sites: e.target.checked })}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LayersIcon />
                  </ListItemIcon>
                  <ListItemText primary="Features" />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={showLayers.features}
                      onChange={(e) => setShowLayers({ ...showLayers, features: e.target.checked })}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <GridIcon />
                  </ListItemIcon>
                  <ListItemText primary="Zones" />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={showLayers.zones}
                      onChange={(e) => setShowLayers({ ...showLayers, zones: e.target.checked })}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <LocationIcon />
                  </ListItemIcon>
                  <ListItemText primary="Labels" />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={showLayers.labels}
                      onChange={(e) => setShowLayers({ ...showLayers, labels: e.target.checked })}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ px: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Legend
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', borderRadius: 1 }} />
                    <Typography variant="body2">Available</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: '#f44336', borderRadius: 1 }} />
                    <Typography variant="body2">Occupied</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: '#ff9800', borderRadius: 1 }} />
                    <Typography variant="body2">Maintenance</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: '#2196f3', borderRadius: 1 }} />
                    <Typography variant="body2">Selected</Typography>
                  </Box>
                </Stack>
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {editMode === 'edit' && (
        <SpeedDial
          ariaLabel="Map actions"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
        >
          {speedDialActions && speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>
      )}

      {/* Site Details Dialog */}
      <SiteDetailsDialog
        open={siteDetailsDialog}
        onClose={() => setSiteDetailsDialog(false)}
        siteDetails={selectedSiteDetails}
      />

      {/* New Map Dialog */}
      <Dialog open={newMapDialog} onClose={() => setNewMapDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Site Map</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Map Name"
                value={newMapForm.name}
                onChange={(e) => setNewMapForm({ ...newMapForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={newMapForm.description}
                onChange={(e) => setNewMapForm({ ...newMapForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Width (pixels)"
                value={newMapForm.width}
                onChange={(e) => setNewMapForm({ ...newMapForm, width: parseInt(e.target.value) || 1200 })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Height (pixels)"
                value={newMapForm.height}
                onChange={(e) => setNewMapForm({ ...newMapForm, height: parseInt(e.target.value) || 800 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewMapDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateMap} variant="contained">Create Map</Button>
        </DialogActions>
      </Dialog>

      {/* Auto Generate Dialog */}
      <Dialog open={autoGenerateDialog} onClose={() => setAutoGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto Generate Site Map</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will automatically arrange all sites for the selected location on a new map
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Map Width"
                value={autoGenerateForm.mapWidth}
                onChange={(e) => setAutoGenerateForm({ ...autoGenerateForm, mapWidth: parseInt(e.target.value) || 1200 })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Map Height"
                value={autoGenerateForm.mapHeight}
                onChange={(e) => setAutoGenerateForm({ ...autoGenerateForm, mapHeight: parseInt(e.target.value) || 800 })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Padding"
                value={autoGenerateForm.padding}
                onChange={(e) => setAutoGenerateForm({ ...autoGenerateForm, padding: parseInt(e.target.value) || 50 })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Spacing"
                value={autoGenerateForm.spacing}
                onChange={(e) => setAutoGenerateForm({ ...autoGenerateForm, spacing: parseInt(e.target.value) || 20 })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoGenerateForm.groupByType}
                    onChange={(e) => setAutoGenerateForm({ ...autoGenerateForm, groupByType: e.target.checked })}
                  />
                }
                label="Group sites by type"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoGenerateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAutoGenerate} 
            variant="contained" 
            startIcon={<AutoGenerateIcon />}
            disabled={loading}
          >
            Generate Map
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialog} onClose={() => setPublishDialog(false)}>
        <DialogTitle>Publish Site Map</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Publishing this map will make it the active map for guest bookings. Any previously published map will be unpublished.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialog(false)}>Cancel</Button>
          <Button onClick={handlePublishMap} variant="contained" color="primary">
            Publish Map
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SiteMapPage;